//* Import react and other packages
import { useEffect, useState } from "react";

//* Import components from react-bootstrap
import { Button, Form, Spinner, Alert, Modal } from "react-bootstrap";

//* Import components
import { Attention } from "../../../component/base/Attention";

//* Import hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//* Import icons from material-ui
import { ArrowBack, SaveRounded } from "@material-ui/icons";

//* Import custom hooks
import { useRegistrations } from "../../../hooks/registration/useRegistration";

export function RegistrationForm() {
  const navigate = useNavigate();
  const params = useParams();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    teamId: "",
    studentId: "",
    isActive: false,
  });

  const [originalData, setOriginalData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState<string>("");

  const { getRegistrationById, updateRegistration, isUpdating } =
    useRegistrations(token);

  // State for modal
  const [showModal, setShowModal] = useState(false);

  //Function to show the modal
  const handleShowModal = () => setShowModal(true);

  //Function to close the modal
  const handleCloseModal = () => setShowModal(false);

  // Function to confirm the action
  const handleConfirm = async () => {
    handleCloseModal();
    try {
      if (params.id) {
        await updateRegistration({
          id: params.id,
          teamId: formData.teamId,
          studentId: formData.studentId,
          isActive: formData.isActive,
        });
        navigate("/registrations");
      }
    } catch (err) {
      console.error("Operação falhou:", err);
      setError("Falha ao atualizar a matrícula.");
    }
  };

  // Fetch registration data
  useEffect(() => {
    const fetchRegistrationData = async () => {
      if (params.id) {
        setIsLoadingData(true);
        try {
          const registrationData = await getRegistrationById(params.id);
          if (registrationData) {
            setFormData({
              teamId: registrationData.teamId || "",
              studentId: registrationData.studentId || "",
              isActive: registrationData.isActive,
            });
            setOriginalData(registrationData);
          }
        } catch (error) {
          console.error("Erro ao buscar dados da matrícula:", error);
          setError("Erro ao buscar dados da matrícula.");
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchRegistrationData();
  }, [params.id, getRegistrationById]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleShowModal();
  };

  const isLoading = isUpdating || isLoadingData;

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <h4 className="mt-0 header-title">Atualizar Matrícula</h4>
            <p>Atualize o status da matrícula do estudante no sistema</p>
            <br />

            {!isLoading && originalData && (
              <Form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label>Nome do Estudante</Form.Label>
                      <Form.Control
                        type="text"
                        value={originalData.student.name}
                        disabled
                      />
                    </Form.Group>
                  </div>

                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label>Email do Estudante</Form.Label>
                      <Form.Control
                        type="email"
                        value={originalData.student.email}
                        disabled
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label>Turma</Form.Label>
                      <Form.Control
                        type="text"
                        value={originalData.team.name}
                        disabled
                      />
                    </Form.Group>
                  </div>

                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label>Curso</Form.Label>
                      <Form.Control
                        type="text"
                        value={originalData.team.course}
                        disabled
                      />
                    </Form.Group>
                  </div>

                  <div className="col-lg">
                    <Form.Group className="mb-3">
                      <Form.Label>Unidade</Form.Label>
                      <Form.Control
                        type="text"
                        value={originalData.team.unit}
                        disabled
                      />
                    </Form.Group>
                  </div>
                </div>

                <hr />

                <div className="d-flex align-items-center mb-4">
                  <Form.Label className="me-3 mb-0">
                    <strong>Status da Matrícula</strong> <Attention />
                  </Form.Label>
                  <Form.Check
                    type="switch"
                    id="isActiveSwitch"
                    label={formData.isActive ? "Ativa" : "Inativa"}
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                </div>

                <div className="w-100 d-flex justify-content-between mt-3">
                  <Button
                    variant="secondary"
                    className="d-flex align-items-center"
                    onClick={() => navigate("/registrations")}
                    type="button"
                  >
                    <ArrowBack className="me-2" /> Voltar
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isLoading}
                    className="d-flex align-items-center"
                  >
                    <SaveRounded className="me-2" /> Atualizar Matrícula
                  </Button>
                </div>
              </Form>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center align-items-center mt-3">
                <Spinner animation="border" variant="primary" role="status" />
              </div>
            )}

            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}

            {/* Modal de Confirmação */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
              <Modal.Header closeButton>
                <Modal.Title>Confirmar Ação</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Tem certeza de que deseja atualizar o status da matrícula?
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleConfirm}>
                  Confirmar
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
}
